<script lang="ts" setup>
import { proximityScoreMap } from '@thecompaniesapi/shared/client'
import { getRootDomainAndFull, isValidFullDomain } from '@thecompaniesapi/url-helpers'
import dayjs from 'dayjs'

const { breakpoints } = useResponsive()
const { width } = useWindowSize()
const { isSpotlightOpened } = useOverlays()
const { showCompanyProfile } = useCompany()
const { t } = useI18n()
const companiesStore = useCompaniesStore()
const actionsStore = useActionsStore()
const listsStore = useListsStore()
const localePath = useLocalePath()
const route = useRoute()
const router = useRouter()
const teamsStore = useTeamsStore()
const userStore = useUserStore()
const { toggleModal } = useOverlays()

const requesting = ref(false)

const placeholder = computed(() => {
  return width.value && breakpoints.isSmaller('md') ? t('searchPlaceholderMobile') : t('searchPlaceholder')
})

const groups = computed(() => {
  const groups: UGroup[] = [
    {
      key: 'features',
      label: t('globals.features'),

      commands: [
        {
          icon: getIcon('companies'),
          id: 'companies',
          label: t('navigation.companies'),
          to: localePath('companies'),
        },
        (
          userStore?.user && listsStore.lists.length
            ? {
                chip: listsStore.unseenActionsCount,
                icon: getIcon('list'),
                id: 'lists',
                label: t('globals.list', 2),
                to: localePath({
                  name: 'companies-lists-listId',
                  params: {
                    listId: listsStore?.firstListWithUnseenActions?.id
                      ? listsStore.firstListWithUnseenActions.id
                      : listsStore.lists?.[0]?.id,
                  },
                }),

                click: async () => {
                  if (
                    String(route.name).startsWith('companies-lists-listId')
                    && listsStore?.firstListWithUnseenActions?.id
                    && Number(route.params.listId) === listsStore.firstListWithUnseenActions.id
                  ) {
                    await listsStore.updateList({
                      lastSeen: true,
                      listId: listsStore.firstListWithUnseenActions.id,
                    })
                  }
                },
              }
            : undefined as any
        ),
        (
          userStore?.user
            ? {
                click: () => {
                  toggleModal({
                    opened: true,
                    type: 'list',
                  })
                },
                icon: getIcon('add'),
                id: 'create-list',
                label: listsStore?.lists?.length ? t('navigation.createList') : t('navigation.createFirstList'),
              }
            : undefined as any
        ),
        {
          icon: getIcon('enrichmentFeature'),
          id: 'enrichment',
          label: t('globals.enrichment'),
          to: localePath('companies-enrichment'),
        },
        {
          icon: getIcon('api'),
          id: 'api',
          label: t('navigation.apiDocumentation'),
          to: localePath('api-slug'),
        },
        {
          icon: getIcon('useCases'),
          id: 'use-cases',
          label: t('navigation.useCases'),
          to: localePath('use-cases'),
        },
        {
          coins: teamsStore.currentTeam?.credits || 0,
          icon: getIcon('settings'),
          id: 'settings',
          label: t('navigation.settings.settings'),
          to: localePath('settings-profile'),
        },
      ].filter(Boolean),
    },
  ]

  if (userStore.user) {
    if (!requesting.value) {
      groups.unshift({
        key: 'companies-search',
        label: (query: string) => query ? `${t('companiesFound')}: "${query}"` : `${t('searchCompanies')}`,
        search: useDebounceFn(
          async (query: string) => {
            if (!query) {
              return []
            }

            let domain: string = query

            try {
              domain = getRootDomainAndFull(domain).full
            }
            catch {}

            const [{ companies: byName }, { companies: byPrompt }] = await Promise.all([
              companiesStore.fetchByName({
                name: domain || query,
                page: 1,
                simplified: true,
                size: 5,
              }),
              companiesStore.fetchByPrompt({
                page: 1,
                prompt: query,
                similarity: proximityScoreMap.close,
                simplified: true,
                size: 5,
              }),
            ])
            const results: any[] = []

            for (const company of [...byName, ...byPrompt]) {
              if (!results.find(result => result.key === company!.domain!.domain)) {
                results.push({
                  avatar: {
                    loading: 'lazy',
                    src: company?.assets?.logoSquare?.src,
                  } as any,
                  click: () => showCompanyProfile(company!.domain!.domain),
                  icon: company?.assets?.logoSquare?.src ? undefined : getIcon('companies'),
                  key: company!.domain!.domain,
                  label: company?.about?.name || company?.about?.nameLegal || company?.domain?.domain,
                })
              }
            }

            return results
          },
          500,
        ),
      })
    }

    groups.unshift({
      key: 'companies-enrich-request',
      label: t('enrichCompanies'),
      search: useDebounceFn(
        async (query: string) => {
          let domain: string = query

          try {
            domain = getRootDomainAndFull(query).full
          }
          catch {}

          if (!isValidFullDomain(domain)) {
            return []
          }

          const actions = await actionsStore.requestAction({
            domains: [domain],
            estimate: true,
            job: 'enrich-companies',
          })

          const totalCost = (actions || []).reduce((acc, action) => acc + (action?.cost || 0), 0)

          if (!totalCost) {
            return []
          }

          return [
            {
              cost: totalCost,
              icon: getIcon('action'),
              id: `action-${domain}`,
              label: domain,

              click: async () => {
                requesting.value = true

                try {
                  await actionsStore.requestAction({
                    domains: [domain],
                    job: 'enrich-companies',
                    type: 'jobs:request',
                  })
                }
                catch (error) {
                  console.error(error)
                }

                requesting.value = false
              },
            },
          ]
        },
        500,
      ),
    })

    if (actionsStore.actions.length) {
      const ongoingActions = actionsStore.actions.filter(action => action.type === 'jobs:request' && ['active', 'pending'].includes(action.status))

      const completedActions = actionsStore
        .actions
        .filter(action => action.type === 'jobs:request' && ['completed', 'failed'].includes(action.status))
        .sort((current, next) => dayjs(current.updatedAt).isAfter(dayjs(next.updatedAt)) ? -1 : 1)
        .slice(0, 5)

      const completedActionsCommands = (completedActions || [])
        .map(
          (action) => {
            const domains = getDomainsForAction(action, true)

            const query: Record<string, string> = {}
            const params: Record<string, string> = {}

            if (action.type === 'jobs:request') {
              query.action = String(action.id)
            }

            if (action.listId) {
              params.listId = String(action.listId)
            }

            if (domains?.length) {
              // Add the first domain as query if there is only one domain
              if (domains.length === 1) {
                query.domain = domains?.[0]
              }

              // Add a subset of domains as group if there are more than 1 completed domain and no action is set
              if (domains?.length > 1 && !query.action) {
                query.domain = domains?.[0]
                query['domains-group'] = domains.slice(0, 10).join(',')
              }
            }

            const to = action.listId
              ? localePath({
                  name: 'companies-lists-listId',
                  params,
                  query,
                })
              : localePath({
                  name: 'companies',
                  query,
                })

            return {
              click: action,
              id: action.id,
              label: action.type,
              to,
            }
          },
        )

      const ongoingActionsCommands = (ongoingActions || []).map(
        action => ({
          click: action,
          id: action.id,
          label: action.type,
        }),
      )

      if (completedActions.length) {
        groups.unshift({
          commands: completedActionsCommands,
          key: 'recent-actions',
          label: t('completedActions'),
        })
      }

      if (ongoingActions.length) {
        groups.unshift({
          commands: ongoingActionsCommands,
          key: 'active-actions',
          label: t('activeActions'),
        })
      }
    }
  }

  return groups
})

onMounted(() => {
  lockScroll(true)
  autofocusInput()

  if (userStore.user) {
    actionsStore.fetchActions({ size: 25 })
  }
})

onUnmounted(() => {
  lockScroll(false)
})

function autofocusInput() {
  const input = document.querySelector<HTMLInputElement>('.js-command-palette-input')

  if (input) {
    input.focus()
  }
}

async function onCommandSelected(commands: UCommand[]) {
  const command = commands[0]

  try {
    if (command.click && typeof command.click === 'function') {
      await command.click()
    }

    if (command.to) {
      await router.push(command.to)
    }
  }
  catch (error) {
    console.error(error)
  }

  // Actions brings back to the spotlight
  if (`${command.id}`.startsWith('action-')) {
    return
  }

  toggleModal({
    opened: false,
    type: 'spotlight',
  })
}

defineShortcuts({
  escape: {
    handler: () => toggleModal({
      opened: false,
      type: 'spotlight',
    }),
    usingInput: true,
  },
  meta_k: {
    handler: () => toggleModal({
      opened: !isSpotlightOpened.value,
      type: 'spotlight',
    }),
    usingInput: true,
  },
})
</script>

<template>
  <Transition
    appear
    enter-active-class="transition-base"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-base"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isSpotlightOpened"
      class="fixed inset-0 !z-[100] flex items-center justify-center bg-gray-100/[0.20] backdrop-blur-lg dark:bg-gray-900/[0.20]"
      @click="toggleModal({ opened: false, type: 'spotlight' })"
    >
      <div class="container relative mx-4 mt-4 flex min-h-[calc(100vh-4rem)] max-w-full items-start justify-start p-4 py-6 sm:py-8 md:py-24 lg:max-w-5xl">
        <div
          v-if="teamsStore?.currentTeam?.credits"
          class="absolute right-6 top-12 z-20 -mt-4 p-2 sm:p-4 md:top-24 md:-mt-2 md:p-6"
        >
          <BaseCoin
            class="hidden lg:flex"
            :credits="teamsStore?.currentTeam?.credits"
            truncate
          />

          <BaseCoin
            class="flex lg:hidden"
            :credits="teamsStore?.currentTeam?.credits"
            hide-suffix
          />
        </div>

        <UCommandPalette
          :autoselect="false"
          :groups="groups"
          :model-value="[]"
          multiple
          nullable
          :placeholder="placeholder"
          :ui="{
            wrapper: 'mx-auto max-w-full lg:max-w-5xl divide-gray-200 dark:divide-gray-800 bg-gray-100 dark:bg-gray-900 rounded-xl backdrop-blur-lg bg-gray-50/[0.80] dark:bg-gray-900/[0.80] ring-1 ring-indigo-400/[0.16]',
            container: 'divide-gray-200 dark:divide-gray-800',
            input: {
              base: 'js-command-palette-input h-14 px-4',
            },
            group: {
              label: 'px-2 my-2 text-gray-600 dark:text-gray-400',
              command: {
                base: 'cursor-default px-2 py-2 w-full',
                active: 'bg-gray-100 dark:bg-gray-800/50 text-gray-900 dark:text-white',
                container: 'gap-3 w-full',
                label: 'w-full',
              },
            },
          }"
          @update:model-value="onCommandSelected"
        >
          <template #features-command="{ command }">
            <div class="flex w-full items-center gap-2">
              <span>
                {{ command.label }}
              </span>

              <UChip
                v-if="command?.chip"
                class="ml-1 [&>*]:!text-white"
                color="red"
                size="xl"
                :text="command.chip > 9 ? '9+' : command.chip"
                variant="subtle"
              />
            </div>
          </template>

          <template #companies-enrich-request-command="{ command }">
            <div class="flex w-full items-center gap-2">
              <div class="flex items-center gap-2">
                {{ t('enrichCompany') }}
                <UBadge
                  :label="command.label"
                  size="xs"
                  variant="subtle"
                />
              </div>

              <span>
                {{ t('for') }}
              </span>

              <div>
                <BaseCoin
                  class="hidden lg:flex"
                  :credits="command.cost"
                  truncate
                />

                <BaseCoin
                  class="flex lg:hidden"
                  :credits="command.cost"
                  hide-suffix
                />
              </div>
            </div>
          </template>

          <template #active-actions-command="{ command }">
            <div class="no-scrollbar flex w-full items-center justify-between gap-2 overflow-x-auto pr-1">
              <div>
                <ActionDisplay
                  :action="command.click"
                  :content="false"
                />
              </div>

              <div class="flex w-full flex-1 items-center justify-end gap-2">
                <ActionDisplay
                  :action="command.click"
                  content
                  retry
                  :text="false"
                />
              </div>
            </div>
          </template>

          <template #active-actions-icon="{ command }">
            <BasePingDot
              class="ml-1.5"
              :color="getActionPingColor(command.click.status)"
              :pinging="['active', 'pending'].includes(command.click.status)"
            />
          </template>

          <template #recent-actions-command="{ command }">
            <div class="no-scrollbar flex w-full flex-1 items-center gap-2 overflow-x-auto pr-1">
              <div>
                <ActionDisplay
                  :action="command.click"
                  :content="false"
                />
              </div>

              <div class="flex w-full flex-1 items-center justify-end gap-2">
                <ActionDisplay
                  :action="command.click"
                  content
                  retry
                  :text="false"
                />
              </div>
            </div>
          </template>

          <template #recent-actions-icon="{ command }">
            <BasePingDot
              class="ml-1.5"
              :color="getActionPingColor(command.click.status)"
              :pinging="['active', 'pending'].includes(command.click.status)"
            />
          </template>
        </UCommandPalette>
      </div>
    </div>
  </Transition>
</template>

<i18n lang="json">
{
  "en": {
    "activeActions": "Active actions",
    "companiesFound": "Companies found",
    "completedActions": "Recent actions",
    "enrichCompanies": "Enrich companies on demand",
    "enrichCompany": "Enrich a company | Enrich companies",
    "searchCompanies": "Search companies by name or prompt",
    "searchPlaceholder": "Search companies or type a domain to enrich it",
    "searchPlaceholderMobile": "Search"
  },
  "es": {
    "activeActions": "Acciones activas",
    "companiesFound": "Empresas encontradas",
    "completedActions": "Acciones recientes",
    "enrichCompanies": "Enriquecer empresas a la demanda",
    "enrichCompany": "Enriquecer una empresa | Enriquecer empresas",
    "searchCompanies": "Buscar empresas por nombre o prompt",
    "searchPlaceholder": "Busca empresas o escribe un dominio para enriquecerlo",
    "searchPlaceholderMobile": "Buscar"
  },
  "fr": {
    "activeActions": "Actions actives",
    "companiesFound": "Entreprises trouvées",
    "completedActions": "Actions récentes",
    "enrichCompanies": "Enrichir des entreprises à la demande",
    "enrichCompany": "Enrichir l'entreprise | Enrichir les entreprises",
    "searchCompanies": "Rechercher des entreprises par nom ou prompt",
    "searchPlaceholder": "Recherchez des entreprises ou tapez un domaine pour l'enrichir",
    "searchPlaceholderMobile": "Rechercher"
  }
}
</i18n>
